/**
 * Traduzione leggibile dei codici "Borsa" (`exchCode`) restituiti da OpenFIGI
 * nel dialog di risoluzione manuale del ticker (F-040/F-047): sono codici
 * interni Bloomberg, non uno standard MIC, e non riconoscibili a occhio da un
 * utente (segnalato dal vivo: Leonardo S.p.A. mostrava "IM" annegato fra
 * altre 12+ righe come "GR"/"GF"/"US"/"PQ", senza modo di sapere quale
 * scegliere).
 *
 * Fonte: il file di riferimento ufficiale pubblicato da OpenFIGI
 * (openfigi.com/assets/content/OpenFIGI_Exchange_Codes-*.csv, verificato dal
 * vivo il 2026-08-20 insieme a una chiamata reale a `/v3/mapping` per l'ISIN
 * di Leonardo, IT0003856405, per riprodurre esattamente il caso segnalato).
 * Copre i codici osservati in quella risposta reale più il listing primario
 * di ognuno dei mercati già modellati in `db/seeds/002-markets.sql`. Un
 * codice non ancora presente qui non è un errore: la funzione ricade sempre
 * sul codice grezzo, mai su una stringa vuota o un'etichetta inventata.
 *
 * Evidenziare il listing primario (obiettivo originale del ticket) è stato
 * scartato dopo aver verificato la risposta reale: `marketSector` e
 * `securityType` sono identici su tutte le righe di uno stesso strumento
 * (sempre "Equity"/"Common Stock"), e `compositeFIGI` raggruppa i venue per
 * area/famiglia di ticker ma non segnala quale sia il mercato "di casa" —
 * per Leonardo sia "LDO/IM" (corretto) sia "FINMF/US" (un ADR OTC, non
 * primario) hanno la stessa relazione figi/compositeFIGI. Nessun campo della
 * risposta OpenFIGI permette di distinguerli in modo affidabile: meglio non
 * evidenziare nulla che segnalare in modo sbagliato un titolo secondario
 * come primario.
 */
export const EXCHANGE_CODE_LABELS: Record<string, string> = {
  // Italia (XMIL)
  IM: 'Borsa Italiana (Milano)',
  IC: 'Borsa Italiana (Milano) — continuo giornaliero',
  IF: 'Borsa Italiana (Milano) — after hours',

  // Germania (XETR) e piazze regionali
  GY: 'Xetra (Germania)',
  GF: 'Francoforte (Germania)',
  GD: 'Düsseldorf (Germania)',
  GS: 'Stoccarda (Germania)',
  GM: 'Monaco di Baviera (Germania)',
  GI: 'Hannover (Germania)',
  GH: 'Amburgo (Germania)',
  GZ: 'Gettex (Germania)',
  GE: 'Xetra EU Stars (Germania)',
  GQ: 'Xetra Stars (Germania)',
  GT: 'Xetra ETF (Germania)',
  LA: 'LSX Exchange (Germania)',
  LU: 'LSX LSSI (Germania)',
  TH: 'Tradegate (Germania)',
  QT: 'Quotrix (Germania)',

  // Stati Uniti: venue singoli e composito OTC
  US: 'Stati Uniti — composito OTC',
  PQ: 'OTC Markets — Pink Sheets (USA)',
  UV: 'OTC US Market (USA)',
  UU: 'OTC Bulletin Board (USA)',
  UN: 'New York Stock Exchange (USA)',
  UW: 'Nasdaq Global Select (USA)',
  UQ: 'Nasdaq Global Market (USA)',
  UR: 'Nasdaq Capital Market (USA)',
  UP: 'NYSE Arca (USA)',
  UA: 'NYSE American (USA)',

  // MTF europei e altre piazze osservate su Leonardo
  IX: 'Cboe Europe CXE (MTF)',
  BQ: 'Equiduct (MTF)',
  S1: 'SIGMA X MTF',
  EO: 'Euro OTC — composito',
  E1: 'Euro OTC APA',
  AV: 'Vienna (Austria)',

  // Listing primario degli altri mercati già modellati in db/seeds/002-markets.sql
  LN: 'London Stock Exchange (Regno Unito)',
  FP: 'Euronext Paris (Francia)',
  NA: 'Euronext Amsterdam (Paesi Bassi)',
  BB: 'Euronext Brussels (Belgio)',
  ID: 'Euronext Dublin (Irlanda)',
  PL: 'Euronext Lisbon (Portogallo)',
  SE: 'SIX Swiss Exchange (Svizzera)',
  JT: 'Tokyo Stock Exchange (Giappone)',
  HK: 'Hong Kong Stock Exchange',
  CG: 'Shanghai Stock Exchange (Cina)',
  CS: 'Shenzhen Stock Exchange (Cina)',
  AN: 'Australian Securities Exchange',
  CT: 'Toronto Stock Exchange (Canada)',
};

/** Nome leggibile se il codice è mappato; altrimenti il codice grezzo così com'è — mai una stringa vuota. */
export function formatExchangeCode(exchCode: string | null): string {
  if (!exchCode) return '—';
  return EXCHANGE_CODE_LABELS[exchCode.toUpperCase()] ?? exchCode;
}
